import { computed } from 'vue'
import { MULTI_SELECT_SEPARATOR } from '@convenia/helpers'
import { SYSTEM_FIELD_TYPE_MULTIPLE } from '@convenia/common-organisms/SystemFields/content/consts/FieldTypes'
import { createFormBuilderSchema, createSystemFieldsSchema } from '@convenia/common-organisms/SystemFields/helpers'
import { resolveCustomFieldValues } from '../helpers/customFields'

const useSystemFields = (params) => {
  const { schema, systemFields, customFields, persistentFields, hasAttachmentFiles } = params || {}
  const formSchema = computed(() => {
    const { documentFiles, ...remainingSchema } = schema?.value || {}
    const availableSystemFields = systemFields?.value || {}
    const systemFieldsSchema = createSystemFieldsSchema(
      remainingSchema,
      availableSystemFields,
      persistentFields,
    )
    const customFieldsSchema = createFormBuilderSchema(customFields?.value)
    const documentFilesField = availableSystemFields.attachment
    const showDocumentFiles = documentFilesField
      && (!documentFilesField.disabled || (hasAttachmentFiles?.value ?? true))

    return {
      ...systemFieldsSchema,
      ...customFieldsSchema,
      ...(showDocumentFiles ? {
        documentFiles: {
          ...documentFiles || {},
          validation: {
            ...documentFiles?.validation || {},
            required: documentFilesField.mandatory,
          },
          disabled: documentFilesField.disabled,
          ...(documentFilesField.disabled ? { removable: false, hideAddBox: true } : {}),
        },
      } : {}),
    }
  })

  const hasEditableFields = computed(() => Object.entries(formSchema.value || {})
    .filter(([ key ]) => key !== 'documentFiles')
    .some(([ , field ]) => !field?.disabled))

  const hasNoFields = computed(() => (
    Object.keys(systemFields?.value || {}).length === 0
    && Object.keys(customFields?.value || {}).length === 0
  ))

  const requiredFields = computed(() => Object.entries({
    ...systemFields?.value || {},
    ...customFields?.value || {},
  })
    .reduce((acc, [ key, field ]) => ([
      ...acc,
      ...(field.mandatory || field.compulsory ? [ key ] : [])
    ]), []))

  const getCardValues = (entity, formatters = {}) => {
    const getFieldValue = (key, field) => {
      const { displayBy, trackBy, options, type } = field
      const value = entity?.[key] !== undefined ? entity[key] : entity?.customFieldValues?.[key]

      if ([ SYSTEM_FIELD_TYPE_MULTIPLE ].includes(type) && Array.isArray(value))
        return value.join(MULTI_SELECT_SEPARATOR)

      if (typeof value === 'boolean' && Array.isArray(options)) {
        const option = options.find((opt) => opt[trackBy || 'value'] === value)

        return option?.[displayBy || 'label'] ?? formatters[key] ?? '-'
      }

      if (displayBy)
        return value?.[displayBy] || formatters[key] || value || '-'

      return formatters[key] ?? value ?? '-'
    }

    const baseSchema = schema?.value || {}
    const availableSystemFields = systemFields?.value || {}
    const hasSystemFields = Object.keys(availableSystemFields).length > 0

    return Object.entries({ ...baseSchema, ...customFields?.value || {} })
      .map(([ key, field ]) => {
        const isSystemField = key in baseSchema
        const hideProp = field?.infoCardProps?.hide
        const baseHide = typeof hideProp === 'function' ? hideProp(entity) : hideProp
        const isPersistent = !!persistentFields?.includes(key) && hasSystemFields
        const isHidden = isSystemField && !(key in availableSystemFields) && !isPersistent

        return {
          ...systemFields?.value?.[key] || {},
          ...field,
          ...field?.infoCardProps || {},
          hide: baseHide || isHidden,
          value: getFieldValue(key, field),
        }
      })
  }

  const getFormValues = (systemFieldValues, customFieldValues) => ({
    ...systemFieldValues,
    ...resolveCustomFieldValues(customFields?.value, customFieldValues),
  })

  return {
    formSchema,
    getCardValues,
    getFormValues,
    requiredFields,
    hasEditableFields,
    hasNoFields,
  }
}

export default useSystemFields
